import React from "react";

const num = (v) => (v == null || !Number.isFinite(v) ? 0 : v);
const fmt = (v, d = 0) => num(v).toFixed(d);
const A0 = -225, A1 = 45; // sweep 270° (SVG angle, 0 = 3 o'clock)
const pol = (cx, cy, r, deg) => [cx + r * Math.cos((deg * Math.PI) / 180), cy + r * Math.sin((deg * Math.PI) / 180)];
// Narrowband O2 voltage → approximate AFR (steep around stoich: 0.45–0.5 V = 14.7).
const NB = [[0, 16.0], [0.1, 15.2], [0.2, 14.9], [0.45, 14.7], [0.5, 14.7], [0.7, 14.4], [0.8, 14.0], [0.9, 13.2], [1.0, 12.5]];
export const narrowAfr = (v) => {
  if (v >= 1) return 12.5;
  for (let i = 1; i < NB.length; i++) if (v <= NB[i][0]) { const [v0, a0] = NB[i - 1], [v1, a1] = NB[i]; return a0 + ((v - v0) / (v1 - v0)) * (a1 - a0); }
  return 12.5;
};
const angOf = (v, max) => A0 + (Math.max(0, Math.min(max, num(v))) / max) * (A1 - A0);

const BigDial = ({ value, max, step, minor, labelDiv = 1, digits, testid, title, unit }) => {
  const cx = 150, cy = 150, R = 136;
  const ticks = [];
  const sub = minor / 2;
  for (let v = 0; v <= max + 1e-9; v += sub) {
    const major = Math.abs(v / step - Math.round(v / step)) < 1e-6;
    const mid = !major && Math.abs(v / minor - Math.round(v / minor)) < 1e-6;
    const a = angOf(v, max);
    const [x1, y1] = pol(cx, cy, R, a);
    const [x2, y2] = pol(cx, cy, R - (major ? 15 : mid ? 9 : 5), a);
    ticks.push(<line key={`t${v}`} x1={x1} y1={y1} x2={x2} y2={y2} stroke="#f3f4f6" strokeWidth={major ? 2.6 : 1.1} />);
    if (major) {
      const [tx, ty] = pol(cx, cy, R - 34, a);
      ticks.push(<text key={`l${v}`} x={tx} y={ty + 6} textAnchor="middle" fill="#f3f4f6" fontSize="17" fontFamily="Arial, sans-serif">{(v / labelDiv).toLocaleString("en-US")}</text>);
    }
  }
  const ang = angOf(value, max);
  const [nx, ny] = pol(cx, cy, R - 6, ang);
  const [bx, by] = pol(cx, cy, 20, ang + 180);
  const [ax1, ay1] = pol(cx, cy, R, A0);
  const [ax2, ay2] = pol(cx, cy, R, A1);
  const w = Math.max(5, String(digits).length);
  return (
    <svg viewBox="-10 -10 320 320" className="w-full h-full" data-testid={testid}>
      <path d={`M ${ax1} ${ay1} A ${R} ${R} 0 1 1 ${ax2} ${ay2}`} stroke="#f3f4f6" strokeWidth="2.6" fill="none" />
      {ticks}
      <text x="150" y="112" textAnchor="middle" fill="#f3f4f6" fontSize="15" fontWeight="700" letterSpacing="2" fontFamily="Arial, sans-serif">{title}</text>
      <text x="150" y="128" textAnchor="middle" fill="#9ca3af" fontSize="11" fontFamily="Arial, sans-serif">{unit}</text>
      <g transform={`skewX(-8) translate(${cy * 0.14} 0)`}>
        <text x="150" y="212" textAnchor="middle" fill="#22d3ee" opacity="0.14" fontSize="30" fontFamily="'Courier New', monospace" fontWeight="700">{"8".repeat(w)}</text>
        <text x="150" y="212" textAnchor="middle" fill="#22d3ee" fontSize="30" fontFamily="'Courier New', monospace" fontWeight="700" style={{ whiteSpace: "pre", filter: "drop-shadow(0 0 3px rgba(34,211,238,.6))" }}>{String(digits).padStart(w, " ")}</text>
      </g>
      <line x1={bx} y1={by} x2={nx} y2={ny} stroke="#ef3b22" strokeWidth="4" strokeLinecap="round" style={{ transition: "all 160ms linear" }} />
      <circle cx={cx} cy={cy} r="7" fill="#0c0e11" stroke="#ef3b22" strokeWidth="3" />
    </svg>
  );
};

const SmallDial = ({ value, max, label, redFrom, dec = 0, testid }) => {
  const cx = 60, cy = 60, R = 47;
  const zone = (from, to, color) => {
    const [x1, y1] = pol(cx, cy, R - 5, angOf(from, max));
    const [x2, y2] = pol(cx, cy, R - 5, angOf(to, max));
    const large = angOf(to, max) - angOf(from, max) > 180 ? 1 : 0;
    return <path d={`M ${x1} ${y1} A ${R - 5} ${R - 5} 0 ${large} 1 ${x2} ${y2}`} stroke={color} strokeWidth="9" fill="none" />;
  };
  const ang = angOf(value, max);
  const [nx, ny] = pol(cx, cy, R - 6, ang);
  const [l1x, l1y] = pol(cx, cy, 4, ang + 90);
  const [l2x, l2y] = pol(cx, cy, 4, ang - 90);
  const [tx, ty] = pol(cx, cy, 10, ang + 180);
  const ticks = [];
  for (let i = 0; i <= 20; i++) {
    const a = angOf((i / 20) * max, max);
    const big = i % 5 === 0;
    const [x1, y1] = pol(cx, cy, R, a);
    const [x2, y2] = pol(cx, cy, R - (big ? 7 : 4), a);
    ticks.push(<line key={i} x1={x1} y1={y1} x2={x2} y2={y2} stroke="#1f2937" strokeWidth={big ? 1.6 : 0.8} />);
  }
  const z = redFrom != null ? redFrom : max * 0.8;
  return (
    <svg viewBox="0 0 120 120" className="w-full h-full" data-testid={testid}>
      <defs>
        <linearGradient id={`b-${testid}`} x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#f9fafb" /><stop offset="50%" stopColor="#9ca3af" /><stop offset="100%" stopColor="#e5e7eb" /></linearGradient>
        <radialGradient id={`g-${testid}`} cx="50%" cy="35%" r="70%"><stop offset="0%" stopColor="#ffffff" /><stop offset="100%" stopColor="#e5e7eb" /></radialGradient>
      </defs>
      <circle cx={cx} cy={cy} r="59" fill={`url(#b-${testid})`} />
      <circle cx={cx} cy={cy} r="53" fill={`url(#g-${testid})`} stroke="#d1d5db" strokeWidth="1" />
      {zone(z - max * 0.15, z, "#f59e0b")}
      {zone(z, max, "#dc2626")}
      {ticks}
      <text x={cx} y="42" textAnchor="middle" fill="#374151" fontSize="9" fontFamily="Arial, sans-serif">{label}</text>
      {[0, 1].map((f) => { const [lx, ly] = pol(cx, cy, R - 15, angOf(f * max, max)); return <text key={f} x={lx} y={ly + 3} textAnchor="middle" fill="#374151" fontSize="8" fontWeight="700" fontFamily="Arial, sans-serif">{Math.round(f * max)}</text>; })}
      <polygon points={`${l1x},${l1y} ${nx},${ny} ${l2x},${l2y} ${tx},${ty}`} fill="#dc2626" style={{ transition: "all 160ms linear" }} />
      <circle cx={cx} cy={cy} r="7" fill="#3b82f6" stroke="#2563eb" strokeWidth="1" />
      <text x={cx} y="104" textAnchor="middle" fill="#111827" fontSize="14" fontFamily="Arial, sans-serif">{fmt(value, dec)}</text>
    </svg>
  );
};

const InfoRow = ({ title, value, align = "right", testid }) => (
  <div className={align === "right" ? "text-right" : "text-left"}>
    <div className="text-white font-bold text-[14px] lg:text-[20px] leading-tight">{title}</div>
    <div className="text-slate-300 text-[12px] lg:text-[17px] font-mono" data-testid={testid}>{value}</div>
  </div>
);

const BarBox = ({ label, value, unit, max, dec = 1, testid }) => (
  <div className="flex items-center gap-2" data-testid={testid}>
    <div className="w-[96px] shrink-0 text-[13px] sm:text-[15px] leading-tight text-slate-300"><div className="font-bold text-white">{label}</div>{fmt(value, dec)} {unit}</div>
    <div className="flex-1 h-7 rounded-sm bg-[#2a1515] overflow-hidden"><div className="h-full bg-[#e52321] transition-[width] duration-150" style={{ width: `${Math.min(100, (num(value) / max) * 100)}%` }} /></div>
  </div>
);

export const RaceDash = ({ sample = {}, peaks = {}, connected, livePoll, ecmId }) => {
  const status = connected ? (livePoll ? "Streaming" : "Connecting") : "Offline";
  const o2 = sample.o2;
  const afr = Number.isFinite(o2) && o2 > 0 ? narrowAfr(o2) : null;
  const mix = afr == null ? null : o2 < 0.45 ? ["KURUS (LEAN)", "text-sky-400"] : o2 <= 0.5 ? ["IDEAL", "text-emerald-400"] : ["KAYA (RICH)", "text-red-400"];
  return (
    <div className="tt-dark flex-1 min-h-0 rounded-md border-2 border-[#4b505b] bg-[#0c0e11] p-2 sm:p-3 flex flex-col gap-2 overflow-hidden" data-testid="race-dash">
      <div className="grid grid-cols-3 sm:grid-cols-6 gap-1 rounded bg-[#08090b] px-2 py-1 text-center shrink-0">
        {[["Status", status, "dash-status"], ["ECU", ecmId || "—", "dash-ecu"], ["Battery", `${fmt(sample.battery, 1)} V`, "dash-bat"], ["O2", `${fmt(sample.o2, 2)} V`, "dash-o2"], ["Injector", `${fmt(sample.injector, 2)} ms`, "dash-inj"], ["Ignition", `${fmt(sample.ignition, 1)}°`, "dash-ign"]].map(([t, v, id]) => (
          <div key={t}><div className="text-white font-bold text-[14px] sm:text-[18px]">{t}</div><div className={`text-[13px] sm:text-[15px] font-mono ${t === "Status" && livePoll ? "text-emerald-400" : "text-slate-300"}`} data-testid={id}>{v}</div></div>
        ))}
      </div>
      <div className="grid grid-cols-12 gap-2 items-center flex-[3] min-h-0">
        <div className="col-span-2 hidden md:flex flex-col gap-3">
          <InfoRow title="Throttle (TPS)" value={`${fmt(sample.tps)} %`} testid="dash-tps" />
          <InfoRow title="Manifold (MAP)" value={`${fmt(sample.map)} kPa`} testid="dash-map" />
          <InfoRow title="Intake Air" value={`${fmt(sample.iat)} °C`} testid="dash-iat" />
          <InfoRow title="Max Speed" value={`${fmt(peaks.speed)} km/h`} testid="dash-max-speed" />
        </div>
        <div className="col-span-5 md:col-span-4 h-full min-h-0 flex items-center justify-center"><div className="h-full max-w-full aspect-square"><BigDial value={sample.speed} max={200} step={20} minor={10} digits={fmt(sample.speed)} title="SPEED" unit="km/h" testid="dash-speedo" /></div></div>
        <div className="col-span-2 md:col-span-1 flex flex-col items-center gap-3">
          <div className="font-mono font-black text-[#ef1c1c] text-[44px] sm:text-[68px] leading-none whitespace-nowrap [text-shadow:0_0_10px_rgba(239,28,28,.6)]" data-testid="dash-afr-digit">{afr == null ? "--.-" : afr.toFixed(1)}</div>
          <div className="text-white text-[14px] sm:text-[16px] font-bold" title="Estimasi dari tegangan sensor O2 narrowband (0,45–0,5 V = 14,7)">AFR</div>
          {mix && <div className={`text-[12px] sm:text-[13px] font-bold -mt-2 ${mix[1]}`} data-testid="dash-afr-status">{mix[0]}</div>}
          <div className="w-20 h-20 sm:w-24 sm:h-24 rounded-full bg-gradient-to-b from-[#ff4a47] to-[#b31512] border-2 border-[#ff9b99] ring-4 ring-[#4a1211] shadow-[0_0_16px_rgba(229,35,33,.45)] flex items-center justify-center text-white text-[16px] sm:text-[18px] font-bold" data-testid="dash-trim-circle">{Number.isFinite(sample.stft) ? `${(sample.stft - 1) * 100 >= 0 ? "+" : ""}${((sample.stft - 1) * 100).toFixed(1)}%` : "--"}</div>
          <div className="text-white text-[14px] sm:text-[16px] font-bold -mt-1">FUEL TRIM</div>
        </div>
        <div className="col-span-5 md:col-span-4 h-full min-h-0 flex items-center justify-center"><div className="h-full max-w-full aspect-square"><BigDial value={sample.rpm} max={14000} step={2000} minor={500} digits={fmt(sample.rpm)} title="RPM" unit="x1000 r/min" labelDiv={1000} testid="dash-tacho" /></div></div>
        <div className="col-span-1 hidden md:flex flex-col gap-3">
          <InfoRow title="Coolant" value={`${fmt(sample.ect)} °C`} align="left" testid="dash-ect" />
          <InfoRow title="Max RPM" value={fmt(peaks.rpm)} align="left" testid="dash-max-rpm" />
        </div>
      </div>
      <div className="grid grid-cols-12 gap-2 items-center flex-[1.5] min-h-0">
        <div className="col-span-12 md:col-span-3 flex flex-col gap-2 order-2 md:order-1">
          <BarBox label="O2" value={sample.o2} unit="V" max={1.2} dec={2} testid="dash-bar-o2" />
          <BarBox label="Injector" value={sample.injector} unit="ms" max={20} dec={2} testid="dash-bar-inj" />
        </div>
        <div className="col-span-12 md:col-span-6 grid grid-cols-3 gap-2 order-1 md:order-2 h-full min-h-0">
          <div className="h-full min-h-0 flex items-center justify-center"><div className="h-full max-w-full aspect-square"><SmallDial value={sample.ect} max={150} redFrom={115} label="Coolant °C" testid="dash-g-ect" /></div></div>
          <div className="h-full min-h-0 flex items-center justify-center"><div className="h-full max-w-full aspect-square"><SmallDial value={sample.map} max={120} label="MAP kPa" testid="dash-g-map" /></div></div>
          <div className="h-full min-h-0 flex items-center justify-center"><div className="h-full max-w-full aspect-square"><SmallDial value={sample.iat} max={100} redFrom={80} label="Intake °C" testid="dash-g-iat" /></div></div>
        </div>
        <div className="col-span-12 md:col-span-3 flex flex-col gap-2 order-3">
          <BarBox label="Ignition" value={sample.ignition} unit="°" max={50} testid="dash-bar-ign" />
          <BarBox label="Battery" value={sample.battery} unit="V" max={16} testid="dash-bar-bat" />
        </div>
      </div>
    </div>
  );
};
